import { ComponentType, lazy, useRef } from "react";

/**
 * A code-split component that renders without a Suspense fallback once its
 * code has been fetched ahead of time with `preload`.
 *
 * React.lazy suspends on its first render even when the module has already
 * arrived, and React then holds the content back for up to 300 ms after the
 * fallback showed (react-dom's FALLBACK_THROTTLE_MS): a preloaded lazy
 * component still costs a flash of the fallback and that delay. `Component`
 * renders the module itself once it is there, and the lazy wrapper only
 * before. The pick is kept for the mount: switching from the wrapper to the
 * module once it lands would remount it.
 */
export function preloadable<P extends object>(
    load: () => Promise<{ default: ComponentType<P> }>,
) {
    let loaded: ComponentType<P> | undefined;
    let loading: Promise<{ default: ComponentType<P> }> | undefined;

    const preload = () =>
        (loading ??= load().then((module) => {
            loaded = module.default;
            return module;
        }));

    const Lazy = lazy(preload);

    function Component(props: P) {
        const Picked = useRef(loaded ?? Lazy).current;
        return <Picked {...props} />;
    }

    return { preload, Component };
}
